/* eslint-disable jsx-a11y/anchor-is-valid */
import DocumentViewerModal from "components/DocumentViewer";
import Loading from "components/Loading";
import ReloadButton from "components/ReloadButton";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import ComplaintInformationTab from "pages/Complaint/fragment/ComplaintInformationTab";
import OrgUnitForm from "pages/Complaint/model/OrgUnitForm";
import ResolutionForm from "pages/Complaint/model/ResolutionForm";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { editAppeal, fetchAppealById } from "services/complaintDetailService";
import {
  Appeal,
  AppealStatus,
  ComplaintReception,
  FILE_URL,
  ResolutionDecisionType,
  Resolutions,
  Roles,
} from "types";
import { LoadingIcon, pdfIcon } from "utils/constant";
import {
  DateView,
  errorHandler,
  errorHandlerOnForm,
  hasRole,
} from "utils/helper";
import AppealRequestForm from "./fragment/AppealRequestForm";
import RegistrarForm from "./fragment/RegistrarForm";

const AppealDetail: React.FC = () => {
  const { showTooltip } = useTooltip();
  const { loginData } = useAuth();
  const params = useParams();
  const navigate = useNavigate();

  const [appeal, setAppeal] = useState<Appeal>();
  const [loading, setLoading] = useState(false);
  const [actionIsLoading, setActionIsLoading] = useState(false);

  const [isOpenAppealRequest, setIsOpenAppealRequest] = useState(false);
  const [isReadyForAppeal, setIsReadyForAppeal] = useState(false);
  const [isOpenRegistrar, setOpenRegistrar] = React.useState(false);
  const [selectedResolution, setSelectedResolution] =
    React.useState<Resolutions | null>(null);

  const isManagement = hasRole(loginData?.roles, [Roles.management]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.complaintRegistrar]);

  const fetchData = (appealId: string) => {
    setLoading(true);
    fetchAppealById(appealId)
      .then((data) => {
        setAppeal(data);
        setIsReadyForAppeal(
          !!data?.resolution_info?.some((r) => r?.registration_number) &&
            !data?.is_appealed,
        );
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (params?.appealId) fetchData(params.appealId);
    // eslint-disable-next-line
  }, [params]);

  const handleStatusChange = (status: keyof typeof AppealStatus) => {
    if (!appeal?.id) return;
    setActionIsLoading(true);
    editAppeal({
      id: appeal.id,
      status,
      complaint_id: appeal.complaint,
      parent_appeal: appeal.parent_appeal_id || appeal?.parent_appeal,
    })
      .then(() => {
        showTooltip("Status updated!", "success");
        fetchData(String(appeal.id));
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setActionIsLoading(false));
  };

  if (loading || !appeal) return <Loading />;

  // Render a detail card for the main appeal only
  const renderAppealCard = (appealData: Appeal) => (
    <div className="flex flex-col gap-2 flex-1 min-w-[350px]">
      {appeal?.complaint_info?.client_id && (
        <div>
          <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <span className="text-sm font-medium text-red-700">
              Application Number:{" "}
            </span>
            <span className="font-bold text-red-900">
              {appeal.complaint_info.client_id}
            </span>
          </div>
        </div>
      )}

      <ComplaintInformationTab
        complaint={appeal?.complaint_info as ComplaintReception}
        setRefetch={() => fetchData(params?.appealId || "")}
      />
    </div>
  );

  const latestResolution = appeal.resolution_info
    ? appeal.resolution_info?.sort(
        (a: any, b: any) =>
          new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime(),
      )[0]
    : null;

  return (
    <div className={`ml-1 mr-4 space-y-6 ${"max-w-screen"}`}>
      <div className="mb-4 flex flex-row items-center gap-5">
        <button
          className="flex items-center gap-2 text-gray-700 hover:text-black"
          onClick={() => navigate(-1)}
        >
          <span className="text-lg">&#8592;</span> Back
        </button>
        <span className="text-2xl font-semibold text-gray-700">
          📝 Appeal Detail Information
        </span>
        <div className="flex-1 flex flex-row justify-end gap-2 text-right">
          <ReloadButton onReload={() => fetchData(params?.appealId || "")} />
          <span className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
            {appeal.status && (
              <span
                className={`
                px-4 py-1 rounded-full text-sm font-bold
                ${
                  appeal.status === "resolved"
                    ? "bg-green-100 text-green-700 border border-green-300"
                    : appeal.status === "pending"
                      ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                      : appeal.status === "closed"
                        ? "bg-gray-200 text-gray-700 border border-gray-300"
                        : appeal.status === "forwarded_to_management"
                          ? "bg-red-100 text-red-700 border border-red-300"
                          : appeal.status === "on_appointment"
                            ? "bg-purple-100 text-purple-700 border border-purple-300"
                            : appeal.status === "appealed"
                              ? "bg-red-100 text-red-700 border border-red-300"
                              : "bg-gray-100 text-gray-700 border border-gray-300"
                }
              `}
              >
                {AppealStatus[appeal.status!]}
              </span>
            )}
            {appeal?.parent_appeal_info && (
              <button
                type="button"
                onClick={() => {
                  navigate("/appeal/" + appeal?.parent_appeal_info?.id + "/");
                }}
                className="border border-[#212121] bg-[#212121] px-4 py-2 text-white cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
              >
                View Prvious Appeal
              </button>
            )}
          </span>

          <button
            className="border border-[#212121] bg-[#212121] px-4 py-2 text-white cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
            onClick={() => navigate(`/complaint/${appeal?.complaint_info?.id}`)}
          >
            Open Compliant
          </button>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-8">
        {/* Left: Appeal Information */}
        <div className="flex-1">
          {renderAppealCard(appeal)}

          {/* Child Appeals List */}
          {appeal.child_appeals_info &&
            appeal.child_appeals_info.length > 0 && (
              <div className="mt-8">
                <div className="font-semibold text-base mb-2">
                  Child Appeals
                </div>
                <div className="flex flex-col gap-2">
                  {appeal.child_appeals_info.map((child, index) => (
                    <div
                      key={child.id}
                      className="flex items-center justify-between border rounded p-3 bg-gray-50"
                    >
                      <div>
                        <div className="font-medium text-ms">
                          Appeal: #{index + 1}
                        </div>
                        <div className="text-xs text-gray-600">
                          Date:{" "}
                          {child.appeal_date
                            ? new Date(child.appeal_date).toLocaleDateString()
                            : "-"}
                        </div>
                        <div className="text-xs text-gray-600">
                          Summary: {child.appeal_summary}
                        </div>
                      </div>
                      <button
                        className="ml-4 px-4 py-1 bg-black text-white rounded hover:bg-black"
                        onClick={() => navigate(`/appeal/${child.id}`)}
                      >
                        More
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
        {/* Right: Status Action Buttons */}
        <div className={"flex-1"}>
          <div className="w-full">
            <div className="flex flex-row items-center gap-3 mb-2">
              <hr className="w-12 border-[#BABABA]" />
              <p className="text-lg text-[#848484]">Appeal Request</p>
              <hr className="flex-1 border-[#BABABA]" />
            </div>
            <div className="bg-white rounded-xl shadow p-6 mx-auto">
              <div className="flex flex-col gap-2 mb-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">
                    Appeal Application Number
                  </span>
                  <span className="font-semibold text-base text-gray-900">
                    {appeal?.application_number ?? "-"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">Appeal Date</span>
                  <span className="font-semibold text-base text-gray-900">
                    {appeal?.appeal_date
                      ? new Date(appeal.appeal_date).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 font-medium">
                    Lower Admin Resolution Date
                  </span>
                  <span className="font-semibold text-base text-gray-900">
                    {appeal?.resolution_date
                      ? new Date(appeal.resolution_date).toLocaleDateString()
                      : "-"}
                  </span>
                </div>
              </div>
              <hr className="my-3" />
              <div className="mb-2">
                <div className="font-bold text-gray-800 mb-1">
                  Appeal Claim Summary
                </div>
                <div className="text-gray-700 text-sm leading-relaxed">
                  {appeal?.appeal_summary ?? "-"}
                </div>
              </div>
              <div className="mb-2">
                <div className="font-bold text-gray-800 mb-1">
                  Lower Administration Unit Resolution
                </div>
                <div className="text-gray-700 text-sm leading-relaxed">
                  {appeal?.resolution_text ?? "-"}
                </div>
              </div>
              <hr className="my-3" />
              <div className="flex flex-col gap-2 mb-3">
                <DocumentViewerModal
                  documents={appeal?.uploaded_files || []}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.APPEAL}`}
                  layout="list"
                  // label="Upload"
                  hideEmptyFile={true}
                />
              </div>
              <div className="flex items-center gap-3 bg-[#F8F6F4] rounded-md p-3 mb-4">
                <img
                  src={require("../../components/assets/person.png")}
                  alt="avatar"
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-sm font-medium text-gray-900">
                  By : {appeal?.resolved_by}
                </span>
              </div>
            </div>
          </div>
          {appeal?.resolution_info && (
            <div className="mt-4">
              <div className="flex flex-row items-center gap-3">
                <hr className="w-12 border-[#BABABA]" />
                <p className="text-lg text-[#848484]">Appeal Resolutions</p>
                <hr className="flex-1 border-[#BABABA]" />
              </div>
              {appeal.resolution_info.map((resolution, index) => {
                return (
                  <div
                    key={index}
                    className="flex flex-col mt-4 border border-[#D4D4D4] bg-white p-4 rounded-lg  justify-between"
                  >
                    <p className="text-base font-semibold  text-[#432101AB]">
                      {resolution?.decision_type &&
                        ResolutionDecisionType[resolution?.decision_type]}
                    </p>
                    {resolution.decision_document && (
                      <div className="flex flex-col md:flex-row gap-4 mt-3 items-center justify-between">
                        <div className="flex flex-col md:flex-row gap-4 items-center">
                          {pdfIcon}
                          <p className="text-[#0C1C2D]">
                            Reason for{" "}
                            {resolution?.decision_type &&
                              ResolutionDecisionType[
                                resolution?.decision_type
                              ]}{" "}
                          </p>
                        </div>
                        {typeof resolution.decision_document === "string" &&
                          resolution.decision_document && (
                            <a
                              href={resolution.decision_document}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#432101]"
                            >
                              <span>view</span>
                            </a>
                          )}
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="font-normal text-base text-[#606060] ">
                        {resolution.decision_summary}
                      </p>
                      <p className="font-normal text-base text-[#606060] ">
                        {resolution.appointment_reason}
                      </p>

                      <div className="flex flex-row justify-between my-3  border-b border-b-1-[#EAEAEA] pb-2">
                        <dt className="font-normal text-base text-[#432101AB]">
                          {resolution?.decision_type &&
                            ResolutionDecisionType[
                              resolution?.decision_type
                            ]}{" "}
                          Date
                        </dt>
                        <dd className="font-semibold text-base text-[#0A1D39]">
                          {DateView(resolution?.appointment_date)}
                        </dd>
                      </div>

                      <DocumentViewerModal
                        documents={
                          (resolution?.uploaded_documents || []) as any
                        }
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.RESOLUTION_PROCESS}`}
                        layout="list"
                        // label="Letter Upload"
                        hideEmptyFile={true}
                      />
                      {resolution?.registration_date && (
                        <div className="flex flex-row justify-between my-3  border-b border-b-1-[#EAEAEA] pb-2">
                          <dt className="font-normal text-base text-[#432101AB]">
                            Registration Date
                          </dt>
                          <dd className="font-semibold text-base text-[#0A1D39]">
                            {DateView(resolution?.registration_date)}
                          </dd>
                        </div>
                      )}
                      {resolution?.registration_date && (
                        <div className="flex flex-row justify-between my-3  border-b border-b-1-[#EAEAEA] pb-2">
                          <dt className="font-normal text-base text-[#432101AB]">
                            Rregistration Number
                          </dt>
                          <dd className="font-semibold text-base text-[#0A1D39]">
                            {resolution?.registration_number ?? "-"}
                          </dd>
                        </div>
                      )}
                      <div className="flex flex-row gap-2 items-center">
                        <img
                          src="/person.png"
                          alt="Person"
                          height={40}
                          width={40}
                        />
                        <p className="font-normal text-base text-[#0A1D39] ">
                          By : {resolution?.resolved_by ?? "-"}
                        </p>
                      </div>
                    </div>
                    {isRegistrar &&
                      appeal?.status === "forwarded_to_registrar" &&
                      appeal?.admin_unit_id === loginData?.admin_unit &&
                      !resolution?.registration_date &&
                      resolution?.decision_type === "resolved" &&
                      !isReadyForAppeal && (
                        <button
                          className="w-ful mt-4 rounded-lg bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 text-white transition-all duration-200 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                          onClick={() => {
                            setOpenRegistrar(true);
                            setSelectedResolution(resolution);
                          }}
                        >
                          <div className="flex items-center justify-center">
                            <svg
                              className="mr-2 h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Add Registration
                          </div>
                        </button>
                      )}
                    {/* {isOpenRegistrar && (
                      <RegistrarForm
                        resolution={resolution}
                        onClose={(reload) => {
                          setOpenRegistrar(false);
                          if (reload) {
                            fetchData(params?.appealId || "");
                          }
                        }}
                      />
                    )} */}
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex flex-col gap-4 justify-start mt-2">
            {/* Actions for parent appeal only */}
            {/* {isClerk && appeal.status === "pending" && (
              <button
                className="border bg-[#212121] px-[76px] py-2 w-full text-white cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]"
                onClick={() => handleStatusChange("forwarded_to_management")}
                disabled={actionIsLoading}
              >
                {actionIsLoading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon}
                    Loading...
                  </div>
                ) : (
                  "Forward to Management"
                )}
              </button>
            )} */}
            {isManagement &&
              (appeal.status === "pending" ||
                appeal.status === "on_appointment" ||
                (appeal?.status === "forwarded_to_management" &&
                  latestResolution?.decision_type ===
                    "forward_to_higher_authority")) &&
              appeal?.admin_unit_id === loginData?.admin_unit &&
              appeal?.org_unit_id === loginData?.org_unit && (
                <div className="relative w-full  bg-white p-8 shadow-lg rounded-lg overflow-y-auto mb-3">
                  <ResolutionForm
                    appeal={appeal || ""}
                    onClose={(refetch) => {
                      if (refetch) fetchData(params?.appealId || "");
                    }}
                  />
                </div>
              )}
            {isManagement &&
              appeal?.org_unit_id &&
              appeal?.status === "resolved" &&
              !appeal?.registration_date && (
                <button
                  type="button"
                  disabled={actionIsLoading}
                  className={`flex items-center justify-center gap-2 mt-4 border bg-[#212121] py-2 w-full 
                                text-white  cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]`}
                  onClick={() => {
                    handleStatusChange("forwarded_to_registrar");
                  }}
                >
                  {actionIsLoading ? (
                    <>
                      <span className="animate-spin">{LoadingIcon}</span>
                      Processing...
                    </>
                  ) : (
                    "Forward to Registrar"
                  )}
                </button>
              )}

            {isRegistrar &&
              appeal?.admin_unit_id === loginData?.admin_unit &&
              appeal?.registration_date &&
              !appeal?.org_unit_id && (
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <span className="w-2 h-5 bg-green-600 mr-2 rounded"></span>
                    Org Unit
                  </h3>
                  <OrgUnitForm
                    isEdit={false}
                    appeal={appeal}
                    onClose={(refetch) => {
                      if (refetch) fetchData(params?.appealId || "");
                    }}
                  />
                </div>
              )}
            {/* {appeal?.status === "forwarded_to_registrar" &&
              !isReadyForAppeal && (
                <button
                  className={`mt-4 border bg-[#212121]  px-[76px] py-2  w-full 
                                  text-white  cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]`}
                  onClick={() => {
                    setOpenRegistrar(true);
                    setSelectedResolution(resolution);
                  }}
                >
                  Add Registration
                </button>
              )} */}
            {isManagement && isReadyForAppeal && appeal?.org_unit_id && (
              <button
                className="w-ful mt-4 rounded-lg bg-gradient-to-r from-red-600 to-red-700 px-4 py-3 text-white transition-all duration-200 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                onClick={() => {
                  setIsOpenAppealRequest(true);
                }}
                disabled={actionIsLoading}
              >
                {actionIsLoading ? (
                  <div className="flex items-center justify-center">
                    {LoadingIcon}
                    Loading...
                  </div>
                ) : (
                  "Request Appeal"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
      {isOpenAppealRequest && (
        <AppealRequestForm
          complaintId={appeal.complaint_info?.id}
          compliant={appeal.complaint_info as ComplaintReception}
          appealId={appeal?.id || ""}
          onClose={() => {
            setIsOpenAppealRequest(false);
          }}
        />
      )}
      {isOpenRegistrar && selectedResolution && (
        <RegistrarForm
          resolution={selectedResolution}
          appeal={appeal}
          onClose={(reload) => {
            setOpenRegistrar(false);
            if (reload) {
              fetchData(params?.appealId || "");
            }
          }}
        />
      )}
    </div>
  );
};

export default AppealDetail;
